import { createFileRoute, useMatch } from '@tanstack/react-router'
import { SETTINGS_STACK } from './-components/settings/settings-section'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useDeferredValue } from 'react'
import { useLingui } from '@lingui/react/macro'
import { JobIcon, UsersIcon } from '@/routes/_app/-components/settings/settings-icons'
import { formatDateLocale, formatNumber } from '@/lib/utils'
import { useTranslation } from '@/lib/locales'
import { useRootSearch } from '@/hooks/use-root-search'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { SandboxResetHint, DeleteJobAction } from './-components/api/entity-row-actions'
import { IntegrationListPanel, IntegrationRow, MetaText } from './-components/api/integration-list'
import { Avatar } from './-components/shared/avatar'
import { CompletionIndicator } from './-components/shared/completion-indicator'
import { IntegrationListPending } from './-components/settings/account-pending'

export const Route = createFileRoute('/_app/account/integrations/$appId/jobs')({
  component: RouteComponent,
  pendingComponent: IntegrationListPending,
  // Painted on the click frame; safe only because the searched list key lives on the pathless `_list` layout below.
})

function RouteComponent() {
  const { language } = useTranslation()
  const { t, i18n } = useLingui()
  const { q } = useRootSearch()
  const { appId } = Route.useParams()
  const { searchInput, handleSearchChange } = useDebouncedSearch({ initialValue: q })

  const { context } = useMatch({ from: '/_app/account/integrations/$appId/jobs/_list' })
  // Defer the OPTIONS object (the context is reference-stable per match), so a new search keeps the
  // previous page on screen while the next one loads instead of flashing the skeleton.
  const deferredOptions = useDeferredValue(context.appJobsQueryOptions)
  const { data: offers, isFetching } = useSuspenseQuery(deferredOptions)
  // True while the deferred options lag the live ones — keeps the dimming through that transition.
  const isStale = isFetching || deferredOptions !== context.appJobsQueryOptions

  const total = offers.pagination.count
  // Plural chosen on the number, shown formatted.
  const count = formatNumber(i18n.locale, total, 0)
  return (
    <div className={SETTINGS_STACK}>
      <SandboxResetHint />
      <IntegrationListPanel
        entity="jobs"
        query={searchInput}
        onQueryChange={handleSearchChange}
        count={total === 1 ? t`${count} job` : t`${count} jobs`}
        isStale={isStale}
        isEmpty={offers.list.length === 0}
        empty={{
          noResults: Boolean(q),
          icon: <JobIcon className="text-muted-foreground size-5" />,
          title: t`No jobs yet`,
          description: t`Positions your integration creates through the API show here.`,
        }}
        pagination={offers.pagination}
      >
        {offers.list.map((elem) => {
          const name = elem.name ?? t`Unnamed`
          return (
            <IntegrationRow
              key={elem.id}
              media={<Avatar size="md" type="job" name={name} forceInitials subtle />}
              title={name}
              meta={
                <>
                  <CompletionIndicator
                    skills={{ completed: elem.described, standard: elem.standard }}
                    values={{ completed: elem.describedValues, standard: elem.standardValues }}
                    traits={{ completed: elem.describedPersonality, standard: elem.standardPersonality }}
                  />
                  <span className="flex shrink-0 items-center gap-1" title={t`Candidates`}>
                    <UsersIcon className="size-3" />
                    {formatNumber(i18n.locale, elem.applicants, 0)}
                  </span>
                  {elem.company && <MetaText>{elem.company}</MetaText>}
                  {elem.created && (
                    <span className="hidden shrink-0 sm:inline">{formatDateLocale(elem.created, language)}</span>
                  )}
                </>
              }
              actions={<DeleteJobAction appClientId={appId} jobId={elem.id} name={name} />}
            />
          )
        })}
      </IntegrationListPanel>
    </div>
  )
}
